import {
    Checkbox,
    Divider,
    Flex,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuList,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { TbChevronDown, TbSearch } from 'react-icons/tb';
import { SettingsNumberInput } from '../../../../components/settings/primitives';

/** Above this many makes the picker gets a search box, as the OEM one does. */
const MAKES_SEARCH_THRESHOLD = 6;

export interface VendorPercentInputProps {
    /** Kept as text so a half-typed value survives (e.g. "1" before "10"). */
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isDisabled?: boolean;
    max?: number;
}

/**
 * Percentage entry, matching the numeric fields on the other settings cards:
 * plain text with a "%" suffix that turns red outside 0–max.
 */
export const VendorPercentInput = ({
    value,
    onChange,
    placeholder = '0',
    isDisabled = false,
    max = 100,
}: VendorPercentInputProps) => (
    <SettingsNumberInput
        value={value}
        onChange={onChange}
        min={0}
        max={max}
        allowDecimals
        width="96px"
        unit="%"
        placeholder={placeholder}
        isDisabled={isDisabled}
        justify="flex-end"
    />
);

export interface VendorMakesSelectProps {
    /** Make ids, or 'all' for every make. */
    value: Array<string> | 'all';
    onChange: (next: Array<string> | 'all') => void;
    options: Array<{ _id: string; name: string }>;
    width?: string;
}

/**
 * Which makes the vendor's terms apply to. "All Makes" is the default and
 * unchecking it opens the list of the org's makes.
 */
export const VendorMakesSelect = ({
    value,
    onChange,
    options,
    width = '160px',
}: VendorMakesSelectProps) => {
    const [search, setSearch] = useState('');

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => option.name.toLowerCase().includes(query));
    }, [options, search]);

    const isAll = value === 'all';
    const selected = isAll ? new Set<string>() : new Set(value);
    const label = isAll
        ? 'All Makes'
        : selected.size === 0
            ? 'No makes'
            : selected.size === 1
                ? options.find((option) => selected.has(option._id))?.name ?? '1 make'
                : `${selected.size} makes`;

    return (
        <Menu closeOnSelect={false} placement="bottom-end">
            <MenuButton
                w={width}
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={3}
                h="32px"
                textAlign="left"
                _hover={{ borderColor: 'gray.300' }}
            >
                <Flex align="center" justify="space-between" gap={2}>
                    <Text fontSize="sm" color="gray.700" noOfLines={1}>
                        {label}
                    </Text>
                    <Icon
                        as={TbChevronDown as React.ElementType}
                        boxSize={3.5}
                        color="gray.400"
                        flexShrink={0}
                    />
                </Flex>
            </MenuButton>
            <MenuList px={3} py={2} minW="240px" maxH="280px" overflowY="auto">
                <VStack align="stretch" spacing={2}>
                    {options.length > MAKES_SEARCH_THRESHOLD && (
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={TbSearch as React.ElementType} color="gray.400" />
                            </InputLeftElement>
                            <Input
                                value={search}
                                placeholder="Search makes"
                                onChange={(e) => setSearch(e.target.value)}
                                borderRadius="md"
                                // The menu treats typing as a jump-to-item
                                // shortcut, so the field has to claim the keys.
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </InputGroup>
                    )}
                    <Checkbox
                        size="sm"
                        colorScheme="partbay-blue"
                        isChecked={isAll}
                        onChange={(e) => onChange(e.target.checked ? 'all' : [])}
                    >
                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                            All Makes
                        </Text>
                    </Checkbox>
                    <Divider />
                    {visibleOptions.length === 0 && (
                        <Text fontSize="sm" color="gray.500" px={1} py={1}>
                            No matches
                        </Text>
                    )}
                    {visibleOptions.map((option) => (
                        <Checkbox
                            key={option._id}
                            size="sm"
                            colorScheme="partbay-blue"
                            isChecked={isAll || selected.has(option._id)}
                            onChange={(e) => {
                                // Leaving "All Makes" starts from every make, so
                                // unchecking one reads the way it looks.
                                const base = isAll
                                    ? options.map((candidate) => candidate._id)
                                    : Array.from(selected);
                                onChange(
                                    e.target.checked
                                        ? Array.from(new Set([...base, option._id]))
                                        : base.filter((id) => id !== option._id),
                                );
                            }}
                        >
                            <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                {option.name}
                            </Text>
                        </Checkbox>
                    ))}
                </VStack>
            </MenuList>
        </Menu>
    );
};
